import { useCallback, useState, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useIdleTimer } from "./useIdleTimer";
import { useMultiStepForm } from "./useMultiStepForm";

const useFormLogic = (steps, apiBaseUrl, timeoutMinutes) => {
  const navigate = useNavigate();
  const hasIdleSubmitted = useRef(false);
  const [validationErrors, setValidationErrors] = useState({});

  const fetchLeads = useCallback(
    async (body, email = false) => {
      const payload = {
        ...body,
        email,
      };

      console.log("🟢 fetchLeads called with payload:", payload);

      try {
        const response = await fetch(`${apiBaseUrl}/v1/api/lead`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        console.log("📩 fetchLeads response received:", response.status);

        if (!response.ok) {
          const errorBody = await response.json();
          console.error("❌ fetchLeads failed:", {
            status: response.status,
            message: errorBody.message,
          });

          throw new Error(
            `HTTP error! status: ${response.status}, message: ${errorBody.message}`
          );
        }

        const data = await response.json();
        console.log("✅ fetchLeads success:", data);
        return data;
      } catch (error) {
        console.error("🔥 fetchLeads exception:", error);
        throw error;
      }
    },
    [apiBaseUrl]
  );

  const onFinish = useCallback(
    async (answers) => {
      try {
        console.log("sending data to server", answers);
        const data = await fetchLeads(answers);
        navigate("/summary", { state: { answers, data } });
      } catch (error) {
        toast.error("Failed to submit data. Please try again.");
      }
    },
    [fetchLeads, navigate]
  );

  const {
    stepIndex,
    currentStep,
    selectedOption,
    setSelectedOption,
    textGridValues,
    setTextGridValues,
    handleBack,
    handleNext,
    isNextDisabled,
    stepsLength,
    answers,
    creditors,
    resetForm,
  } = useMultiStepForm(steps, onFinish, fetchLeads, navigate);

  const validateField = useCallback((field, val) => {
    if (!field.required && (!val || val.trim() === "")) return "";

    if (field.required && (!val || val.trim() === "")) {
      return `${field.label || "This"} field is required`;
    }

    if (typeof field.validate === "function") {
      const result = field.validate(val);
      if (result !== true) return result;
    }

    if (field.type === "dropdown" && val !== "California") {
      toast.error("We currently only handle lawsuits filed in California.", {
        position: "bottom-center",
        autoClose: 2000,
      });
      return "We only support lawsuits filed in California";
    }

    if (field.type === "email" && val) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(val)) {
        return "Please enter a valid email address";
      }
    }

    return "";
  }, []);

  const handleFieldBlur = useCallback(
    (fieldLabel) => {
      const val = textGridValues[fieldLabel] || "";
      const field = currentStep.fields?.find((f) =>
        typeof f === "string" ? f === fieldLabel : f.label === fieldLabel
      );
      if (!field) return;

      const error = validateField(
        typeof field === "string" ? { label: field } : field,
        val
      );

      setValidationErrors((prev) => ({ ...prev, [fieldLabel]: error }));
    },
    [textGridValues, validateField, currentStep]
  );

  useIdleTimer({
    onIdle: async () => {
      if (hasIdleSubmitted.current) return;
      hasIdleSubmitted.current = true;

      toast.warn("Your session has expired. Please start again", {
        position: "bottom-center",
        autoClose: 10000,
        style: {
          fontSize: "12px",
          maxWidth: "600px",
          margin: "auto",
          textAlign: "center",
        },
      });

      const currentAnswers = { ...answers };
      resetForm();

      try {
        const data = await fetchLeads(currentAnswers, true);
        console.log("Idle submission response:", data);
      } catch {
        toast.error("Failed to auto-submit data after inactivity.");
      }
    },
    onActive: () => {
      hasIdleSubmitted.current = false;
    },
    timeout: timeoutMinutes * 60 * 1000,
    isEnabled: stepIndex > 1,
  });

  /*
  // Optional: Validate all fields in the step before next
  const validateCurrentStepFields = () => {
    const errors = {};
    currentStep.fields?.forEach((f) => {
      const label = typeof f === "string" ? f : f.label;
      const value = textGridValues[label] || "";
      const fieldObj = typeof f === "string" ? { label: f } : f;
      const err = validateField(fieldObj, value);
      if (err) errors[label] = err;
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  */

  return {
    stepIndex,
    currentStep,
    selectedOption,
    setSelectedOption,
    textGridValues,
    setTextGridValues,
    handleBack,
    handleNext,
    isNextDisabled,
    stepsLength,
    answers,
    creditors,
    resetForm,
    validationErrors,
    handleFieldBlur,
    onFinish,
    fetchLeads,
  };
};

export default useFormLogic;
